import { NavLink } from 'react-router'
import type { ChatView } from '@olmt/shared'
import { useChatStore } from '../stores/chatStore'
import { chatAvatarUrl, chatTitle, isChatOnline, messagePreview } from '../lib/chat'
import { formatListStamp } from '../lib/time'
import { useT } from '../lib/i18n'
import { Avatar } from './Avatar'
import { PhoneIcon, PinIcon } from './Icons'

type ChatListItemProps = { chat: ChatView; meId: string | null }

/**
 * One door in the hall. The open one is marked by a brass rule on the inline-start edge rather than
 * a colour wash alone — a rule is visible in peripheral vision down a long list, and it flips with
 * the writing direction instead of sticking to the left.
 */
export function ChatListItem({ chat, meId }: ChatListItemProps) {
  const t = useT()
  const title = chatTitle(chat, meId)
  const online = useChatStore((state) => isChatOnline(chat, meId, state.presence))
  const typing = useChatStore((state) => Object.keys(state.typing[chat.id] ?? {}).length > 0)
  const unread = chat.unreadCount

  return (
    <li className="chat-row">
      <NavLink
        to={`/chat/${chat.id}`}
        className={({ isActive }) =>
          `relative flex min-h-[72px] items-center gap-3 px-4 py-2.5 transition-colors duration-quick ${
            isActive ? 'bg-surface2' : 'hover:bg-surface2/50'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                aria-hidden="true"
                className="absolute start-0 top-2.5 bottom-2.5 w-[3px] rounded-e-full bg-accent"
              />
            )}
            <Avatar name={title} path={chatAvatarUrl(chat, meId)} size={48} online={online} />
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-body font-semibold text-ink">
                  <bdi>{title}</bdi>
                </span>
                {/* W5: a pinned chat is marked by a glyph, and the glyph is `aria-hidden` — the
                    row's accessible name carries the word (the `sr-only` below), the same split
                    the live pill uses. The pin is the row's *first* sort key, so the mark is here
                    to say why this row sits above newer ones. */}
                {chat.pinnedAt !== null && (
                  <span className="flex shrink-0 items-center self-center text-muted">
                    <PinIcon size={14} />
                    <span className="sr-only">{t('chat.pinned')}</span>
                  </span>
                )}
                {chat.lastMessageAt !== null && (
                  <span className={`numeric shrink-0 text-meta ${unread > 0 ? 'text-accent' : 'text-muted'}`}>
                    {formatListStamp(chat.lastMessageAt)}
                  </span>
                )}
              </span>
              <span className="mt-0.5 flex items-center gap-2">
                {/* The preview is a paragraph of its own: an Arabic preview has to ellipsise at its
                    own end, not at the container's. */}
                <span className={`min-w-0 flex-1 truncate text-ui ${typing ? 'text-accent' : 'text-muted'}`}>
                  <bdi>{typing ? t('chat.typingIndicator') : messagePreview(chat.lastMessage, meId)}</bdi>
                </span>
                {chat.hasActiveCall && (
                  <span className="flex shrink-0 items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-meta font-medium text-accent ring-1 ring-accent/30 ring-inset">
                    <PhoneIcon size={12} />
                    {/* The chip carries a number, not just the word: "live" alone spends the row's
                        whole information budget on a state and says nothing usable (§10.7 item 2).
                        The count is the chat's members, which is what the client actually knows —
                        the SFU's live participant list never reaches the chat list — and the
                        accessible name says so rather than letting "live · 5" imply five people
                        are in the call. */}
                    <span aria-hidden="true" className="numeric">
                      {t('chat.liveCount', { count: chat.memberCount })}
                    </span>
                    <span className="sr-only">
                      {t(chat.memberCount === 1 ? 'chat.callInProgressOne' : 'chat.callInProgressOther', {
                        count: chat.memberCount,
                      })}
                    </span>
                  </span>
                )}
                {unread > 0 && (
                  <span className="numeric flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-accent px-1.5 text-meta font-semibold text-on-accent">
                    {unread > 99 ? '99+' : unread}
                    <span className="sr-only">{' '}{t('chat.unreadMessagesSuffix')}</span>
                  </span>
                )}
              </span>
            </span>
          </>
        )}
      </NavLink>
    </li>
  )
}
